import {
  Canvas,
  FabricObject,
  FabricText,
  Group,
  loadSVGFromURL,
  Pattern,
  Point as FabricPoint,
  Polygon,
  util as FabricUtil
} from 'fabric';
import { MapObject } from './map-object';
import { Observable, Subject } from 'rxjs';
import { Point } from '@ng-cloud/badger-core/models/interfaces/point';
import { Zone } from '@ng-cloud/badger-core/models/interfaces/zone';
import { ApiModel, PolygonUtilities } from '@ng-cloud/badger-core';
import { NavZone } from '../../models/nav-zone';
import { StoreZone } from '../../models/store-zone';
import { ScanZone } from '../../models/scan-zone';
import * as _ from 'lodash';

export class ZoneObject extends MapObject {
  static readonly minSize = 12.5;

  defaultOptions: any = {
    opacity: 1.0,
    selectable: false,
    evented: false,
    lockScalingFlip: true,
    cornerSize: 16,
    cornerColor: 'rgb(10 141 255)',
    borderColor: 'rgb(10 141 255)',
    borderScaleFactor: 2.5,
    padding: 4,
    perPixelTargetFind: true,
    polygonOptions: {
      fill: 'blue',
      opacity: 0.20,
      strokeWidth: 1,
      stroke: '#000000',
      perPixelTargetFind: true
    },
    labelOptions: {
      fontSize: 12,
      fontFamily: 'Roboto, "Helvetica Neue", sans-serif',
      originX: 'center',
      originY: 'center',
      textAlign: 'center',
      textBackgroundColor: '#484b4fAA',
      fill: 'white',
      perPixelTargetFind: true
    },
    zoneVisible: true,
    labelVisible: true
  };

  private polygon: Polygon;
  private symbolPoly?: Polygon; // overlay for patterns (speed zones)
  private zoneGroup: Group;
  private zoneLabel: FabricText;
  private minScaleX: number;
  private minScaleY: number;
  private static arrowFast?: Group;
  private static arrowSlow?: Group;
  private static arrowsLoaded = false;
  private static arrowsLoading?: Promise<void>;

  private getBaseline(): number {
    return (this.getOptions() as any).speedBaseline ?? 1;
  }

  private getDPR(): number {
    return (window as any)?.devicePixelRatio || 1;
  }

  private getZoom(): number {
    const c = (this.zoneGroup?.canvas || (this.polygon as any)?.canvas) as Canvas | undefined;
    if (!c) {
      return 1;
    }
    // Fabric v5+
    if (typeof (c as any).getZoom === 'function') {
      return (c as any).getZoom();
    }
    // Older / direct transform
    const vpt = c.viewportTransform as number[] | undefined;
    return vpt ? vpt[0] : 1;
  }

// snap to integer device pixels to avoid half-pixel blur
  private i(n: number) {
    return Math.round(n);
  }


  private zoneSource: Subject<ZoneObject> = new Subject<ZoneObject>();
  zoneChanged: Observable<ZoneObject> = this.zoneSource.asObservable();

  constructor(public zone: ApiModel & Zone, private points: Point[], config?: any) {
    super(config);
  }

  destroy() {
    this.zoneSource.complete();
    super.destroy();
  }

  // ---------- label helpers ----------
  private formatSpeed(n?: number): string {
    if (typeof n !== 'number' || isNaN(n)) {
      return '';
    }
    return Number(n).toFixed(1).replace(/\.0$/, '');
  }

  private navZoneLabel(nz: NavZone): string {
    if (nz.description?.trim()) {
      return nz.description.trim();
    }
    switch (nz.mode) {
      case NavZone.KEEP_IN_MODE:
        return 'Keep In';
      case NavZone.KEEP_OUT_MODE:
        return 'Keep Out';
      case NavZone.SPEED_MODE: {
        const v = this.formatSpeed(nz.speed_mps);
        return v ? `Speed Zone (${v} m/s)` : 'Speed Zone';
      }
      default:
        return _.startCase(nz.mode);
    }
  }

  public applySpeedBaseline(baseline: number) {
    (this.getOptions() as any).speedBaseline = baseline;
    this.rebuildSymbolPattern();     // refresh repeated symbols
    this.render();
  }

  // ---------- pattern helpers (geometric, no emojis) ----------
  // Normalize |speed - 1| to [0..1]
  private magnitude(speed?: number): number {
    const base = this.getBaseline(), min = 0.1, max = 1.5;
    if (typeof speed !== 'number' || isNaN(speed)) {
      return 0;
    }
    const s = Math.min(Math.max(speed, min), max);
    return s >= base ? (s - base) / (max - base) : (base - s) / (base - min);
  }

  // Dotted pattern for slow zones
  private makeDotPattern(mag: number): Pattern {
    const clamped = Math.min(Math.max(mag, 0), 1);
    const tile = Math.round(44 - 26 * clamped); // 18..44 px (denser as mag→1)
    const c = FabricUtil.createCanvasElement();
    c.width = c.height = tile;
    const ctx = c.getContext('2d')!;
    const r = Math.max(2, Math.round(tile * 0.18));
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.arc(tile / 2, tile / 2, r, 0, Math.PI * 2);
    ctx.fill();
    return new Pattern({ source: c, repeat: 'repeat' } as any);
  }

  // Vertical-bar pattern for fast zones
  private makeBarPattern(mag: number): Pattern {
    const clamped = Math.min(Math.max(mag, 0), 1);
    const tile = Math.round(44 - 26 * clamped); // 18..44 px
    const c = FabricUtil.createCanvasElement();
    c.width = c.height = tile;
    const ctx = c.getContext('2d')!;
    const w = Math.max(2, Math.round(tile * 0.16));
    const h = Math.round(tile * 0.8);
    ctx.fillStyle = 'rgba(0,0,0,0.22)';
    ctx.fillRect(Math.floor((tile - w) / 2), Math.floor((tile - h) / 2), w, h);
    return new Pattern({ source: c, repeat: 'repeat' } as any);
  }

  protected build(): FabricObject {
    const options = this.getOptions();

    this.polygon = new Polygon(this.points, options.polygonOptions);
    this.polygon.set({ data: this.zone });

    // Build label
    let description: string;
    if (this.zone instanceof NavZone) {
      description = this.navZoneLabel(this.zone as NavZone);
    }
    else if (this.zone instanceof StoreZone) {
      description = this.zone.zoneTemplate ? this.zone.zoneTemplate.zoneName.description : '';
    }
    else if (this.zone instanceof ScanZone) {
      description = this.zone.name ? this.zone.name : '';
    }
    else {
      description = _.get(this.zone, 'description', '');
    }
    const padded = description ? ` ${description} ` : '';

    this.zoneLabel = new FabricText(padded, options.labelOptions);
    const labelCenter = PolygonUtilities.getLabelCenter(this.points, 1);
    this.zoneLabel.left = labelCenter.x;
    this.zoneLabel.top = labelCenter.y;

    const zoneObjects: any[] = [];
    options.zoneVisible && zoneObjects.push(this.polygon);
    options.labelVisible && zoneObjects.push(this.zoneLabel);

    this.zoneGroup = new Group(zoneObjects, options);

    // Pattern overlay for speed zones (dots for slow, bars for fast)
    const mode = (this.zone as any)?.mode;
    const isSpeed = mode === NavZone.SPEED_MODE || mode === 'speed';
    if (isSpeed) {
      const s = (this.zone as any).speed_mps ?? this.getBaseline();
      const baseOpacity = (options.polygonOptions?.opacity ?? 0.30);
      const symbolOpacity = Math.max(baseOpacity + 0.1, 0.60);

      // Clone the base polygon so geometry/offsets match perfectly in group coords
      const polyClone = _.cloneDeep(this.polygon);

      this.symbolPoly = polyClone;
      // TEMP: solid red to prove overlay is visible and aligned
      polyClone.set({
        fill: 'rgba(255,0,0,0.35)',
        opacity: symbolOpacity,
        strokeWidth: 0,
        selectable: false,
        evented: false,
        perPixelTargetFind: false,
        objectCaching: false
      });

      // Insert overlay just above the base polygon (index 1), below label
      const objs = (this.zoneGroup as any)._objects as FabricObject[];
      const label = objs.pop();          // temporarily remove label
      objs.splice(1, 0, polyClone);          // [polygon, symbol, ...]
      if (label) {
        objs.push(label);
      }       // restore label
      this.zoneGroup.add();    // recompute bbox

      this.zoneGroup.canvas?.requestRenderAll();

      // Later swap to pattern (once working):
      // ZoneObject.ensureArrowsLoaded().then(() => this.rebuildSymbolPattern());

    }

    this.minScaleX = ZoneObject.minSize / this.zoneGroup.width;
    this.minScaleY = ZoneObject.minSize / this.zoneGroup.height;

    return this.zoneGroup;
  }

  protected setup(): void {
    this.getFabric().on('moving', () => this.notifyChange());
    this.getFabric().on('rotating', () => this.notifyChange());
    this.getFabric().on('scaling', () => {
      this.setFill();
      this.notifyChange();
    });
    this.getFabric().on('modified', () => {
      this.zoneGroup.set({
        scaleX: Math.max(this.zoneGroup.scaleX, this.minScaleX),
        scaleY: Math.max(this.zoneGroup.scaleY, this.minScaleY)
      });
      this.zoneGroup.setCoords();
      this.polygon.set('fill', this.getOptions().polygonOptions.fill);
      this.render();
      this.notifyChange();
    });

    this.getFabric().on('added', () => this.rebuildSymbolPattern());

    this.zoneChanged.subscribe(() => {
      const labelScaleX = this.zoneGroup.width / (this.zoneGroup.width * this.zoneGroup.scaleX);
      const labelScaleY = this.zoneGroup.height / (this.zoneGroup.height * this.zoneGroup.scaleY);
      this.zoneLabel.set({ angle: -this.zoneGroup.angle, scaleX: labelScaleX, scaleY: labelScaleY });
    });
  }

  protected notifyChange() {
    this.zoneSource.next(this);
  }

  getPoints(): Point[] {
    const matrix = this.polygon.calcTransformMatrix();
    return this.polygon.points
      .map((pt) => new FabricPoint(pt.x - this.polygon.pathOffset.x, pt.y - this.polygon.pathOffset.y))
      .map((pt) =>  pt.transform(matrix));
  }

  protected setFill() {
    const color = this.isValid() ? this.getOptions().polygonOptions.fill : 'gray';
    if (color !== this.polygon.fill) {
      this.polygon.set('fill', color);
      this.render();
    }
  }

  private isValid() {
    return this.zoneGroup.scaleX >= this.minScaleX && this.zoneGroup.scaleY >= this.minScaleY;
  }

  static isValidZone(width: number, height: number): boolean {
    return width >= ZoneObject.minSize && height >= ZoneObject.minSize;
  }

  getBaseShape(): FabricObject {
    const group = this.getFabric() as Group;
    return group.getObjects()[0] as FabricObject;
  }

  // -------- overlap clipping (group-level) --------
  cloneShapeForClip(clonedBase: (clone: FabricObject) => void) {
    const abs = this.getPoints(); // [{x,y} in canvas coords]

    const minX = Math.min(...abs.map(p => p.x));
    const minY = Math.min(...abs.map(p => p.y));
    const rel = abs.map(p => ({ x: p.x - minX, y: p.y - minY }));

    const clip = new Polygon(rel, {
      left: minX,
      top: minY,
      originX: 'left',
      originY: 'top',
      fill: '#000',
      strokeWidth: 0,
      selectable: false,
      evented: false,
      objectCaching: false
    });

    clip.setCoords();
    clonedBase(clip);
  }

  applyOverlapClipFrom(hiders: FabricObject[]) {
    const group = this.getFabric();  // clip the whole group (shape + pattern + label)
    if (!hiders.length) {
      group.clipPath = undefined;
      group.dirty = true;
      group.canvas?.requestRenderAll();
      return;
    }

    group.clipPath = new Group(hiders, {
      inverted: true as any,
      absolutePositioned: true as any
    });

    group.dirty = true;
    group.canvas?.requestRenderAll();
  }

  // private makeArrowPatternFromSVG(kind: 'slow' | 'fast'): Pattern {
  //   const dpr = this.getDPR();
  //   const zoom = this.getZoom();
  //
  //   // calm, fixed density so zones are comparable
  //   const TILE_CSS = 24; // tweak 64–80 if you want sparser/denser
  //   const tile = Math.round(TILE_CSS * dpr * zoom);
  //
  //   const sc = new StaticCanvas(null, { width: tile, height: tile });
  //
  //   // if SVGs not loaded yet, draw a blank tile; we'll rebuild after load
  //   const base = (kind === 'slow' ? ZoneObject.arrowSlow : ZoneObject.arrowFast)?.clone() as Group;
  //   if (!base) {
  //     const blank = FabricUtil.createCanvasElement();
  //     blank.width = blank.height = tile;
  //     return new Pattern({ source: blank, repeat: 'repeat', scaleX: 1 / (dpr * zoom), scaleY: 1 / (dpr * zoom) } as any);
  //   }
  //
  //   const targetW = tile * 0.58;                               // arrow length in tile
  //   const targetH = tile * (kind === 'slow' ? 0.26 : 0.16);    // slow = fatter
  //
  //   let bw = base.width || 0, bh = base.height || 0;
  //   if (!bw || !bh) {
  //     const bb = base.getBoundingRect(true, true);
  //     bw = bb.width || 1;
  //     bh = bb.height || 1;
  //   }
  //
  //   base.set({
  //     originX: 'center',
  //     originY: 'center',
  //     left: tile / 2,
  //     top: tile / 2,
  //     angle: 30,
  //     scaleX: targetW / bw,
  //     scaleY: targetH / bh
  //   });
  //   base.setCoords();
  //
  //   // ADA-friendly styling: slow = filled; fast = outline (don’t rely on private _objects)
  //   const strokeW = Math.max(2, Math.round(1.5 * dpr * zoom));
  //   if ((base as any).forEachObject) {
  //     (base as any).forEachObject((o: any) => {
  //       o.set({
  //         stroke: 'rgba(17,17,17,0.95)',
  //         strokeWidth: strokeW,
  //         strokeUniform: true as any,
  //         fill: kind === 'slow' ? 'rgba(255,255,255,0.60)' : 'rgba(255,255,255,0.00)'
  //       });
  //     });
  //   }
  //   else {
  //     (base as any).set({
  //       stroke: 'rgba(17,17,17,0.95)',
  //       strokeWidth: strokeW,
  //       strokeUniform: true as any,
  //       fill: kind === 'slow' ? 'rgba(255,255,255,0.60)' : 'rgba(255,255,255,0.00)'
  //     });
  //   }
  //
  //   sc.add(base);
  //   sc.renderAll();
  //
  //   // scale the tile back so arrow size is constant on screen
  //   return new Pattern({
  //     source: sc.getElement(),
  //     repeat: 'repeat',
  //     scaleX: 1 / (dpr * zoom),
  //     scaleY: 1 / (dpr * zoom)
  //   } as any);
  // }


// simple linear interpolation
  private lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }

  private roundedRectPath(ctx: CanvasRenderingContext2D, w: number, h: number, r: number) {
    const hw = w / 2, hh = h / 2;
    const rr = Math.max(0, Math.min(r, Math.min(hw, hh)));
    ctx.beginPath();
    ctx.moveTo(-hw + rr, -hh);
    ctx.lineTo(hw - rr, -hh);
    ctx.quadraticCurveTo(hw, -hh, hw, -hh + rr);
    ctx.lineTo(hw, hh - rr);
    ctx.quadraticCurveTo(hw, hh, hw - rr, hh);
    ctx.lineTo(-hw + rr, hh);
    ctx.quadraticCurveTo(-hw, hh, -hw, hh - rr);
    ctx.lineTo(-hw, -hh + rr);
    ctx.quadraticCurveTo(-hw, -hh, -hw + rr, -hh);
    ctx.closePath();
  }

  public rebuildSymbolPattern() {
    if (!this.symbolPoly) {
      return;
    }
    const s = (this.zone as any).speed_mps ?? this.getBaseline();
    this.symbolPoly.dirty = true;
    this.zoneGroup?.canvas?.requestRenderAll();
  }

  static ensureArrowsLoaded(): Promise<void> {

    if (ZoneObject.arrowsLoaded) {
      return Promise.resolve();
    }
    if (ZoneObject.arrowsLoading) {
      return ZoneObject.arrowsLoading;
    }

    ZoneObject.arrowsLoading = new Promise<void>((resolve) => {
      // already injected as groups?
      if (ZoneObject.arrowFast && ZoneObject.arrowSlow) {
        ZoneObject.arrowsLoaded = true;
        console.info('[ZoneObject] using preconfigured arrow groups');
        return resolve();
      }

      const fastUrl = ZoneObject.arrowFastUrl;
      const slowUrl = ZoneObject.arrowSlowUrl;
      if (!fastUrl || !slowUrl) {
        console.warn('[ZoneObject] arrow URLs not set; patterns will be blank');
        return resolve();
      }

      const fail = (which: string, url: string, err?: any) => {
        console.error(`[ZoneObject] Failed to load ${which} SVG`, { url, err });
        resolve(); // don’t hang; we’ll just render blanks
      };

      console.info('[ZoneObject] loading arrows', { fastUrl, slowUrl });

      loadSVGFromURL(fastUrl, undefined, { crossOrigin: 'anonymous' }).then((output) => {
        if (!output.objects?.length) {
          return fail('fast', fastUrl);
        }
        ZoneObject.arrowFast = FabricUtil.groupSVGElements(output.objects, output.options) as Group;

        loadSVGFromURL(slowUrl, undefined, { crossOrigin: 'anonymous' }).then((output) => {
          if (!output.objects?.length) {
            return fail('slow', slowUrl);
          }
          ZoneObject.arrowSlow = FabricUtil.groupSVGElements(output.objects, output.options) as Group;
          ZoneObject.arrowsLoaded = true;
          console.info('[ZoneObject] arrows loaded');
          resolve();
        });
      });
    });

    return ZoneObject.arrowsLoading!;
  }

  private static arrowFastUrl?: string;
  private static arrowSlowUrl?: string;

  static configureArrows(opts: {
    fastUrl?: string; slowUrl?: string;
    fastGroup?: Group; slowGroup?: Group;
  }) {
    if (opts.fastGroup && opts.slowGroup) {
      ZoneObject.arrowFast = opts.fastGroup;
      ZoneObject.arrowSlow = opts.slowGroup;
      ZoneObject.arrowsLoaded = true;
      ZoneObject.arrowsLoading = undefined;   // <-- add this
      console.info('[ZoneObject] arrows configured via groups');
      return;
    }
    if (opts.fastUrl) {
      ZoneObject.arrowFastUrl = opts.fastUrl;
    }
    if (opts.slowUrl) {
      ZoneObject.arrowSlowUrl = opts.slowUrl;
    }

    // Allow a fresh load attempt after URLs are set
    ZoneObject.arrowsLoaded = false;
    ZoneObject.arrowsLoading = undefined;     // <-- add this

    console.info('[ZoneObject] arrow URLs set', {
      fast: ZoneObject.arrowFastUrl, slow: ZoneObject.arrowSlowUrl
    });
  }
}
